import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Download, Printer, Share2, ArrowLeft, Inbox } from 'lucide-react';
import axios from 'axios';
import { useLead, useLeads, useLeadsLoaded } from '../lib/leadsStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

function num(v, digits = 3, suffix = '%') {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return '—';
  return `${n.toFixed(digits)}${suffix}`;
}

function fullName(lead) {
  return (lead?.name && lead.name.trim())
    || [lead?.firstName, lead?.middleName, lead?.lastName].filter(Boolean).join(' ').trim()
    || '—';
}

function propertyAddressOf(lead) {
  const a = lead?.application || {};
  const parts = [
    a.propertyAddress || lead?.propertyAddress,
    a.propertyUnit,
    [a.propertyCity, a.propertyState, a.propertyZip].filter(Boolean).join(', '),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export default function LoanSummary() {
  const { id } = useParams();
  const navigate = useNavigate();
  const lightLead = useLead(id);
  const leadsLoaded = useLeadsLoaded();
  const allLeads = useLeads();

  // The leadsStore caches the LIGHT payload (no `application` blob) so the
  // /leads list stays fast at 8k+ scale. Loan Summary needs the FULL lead
  // (borrower DOB, loanInfo, payment breakdown) so we fetch the full doc
  // once per id and merge it on top of the light cache.
  const [fullLead, setFullLead] = useState(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setFetching(true);
    axios
      .get(`${BACKEND_URL}/api/leads/${id}`)
      .then((r) => { if (!cancelled) setFullLead(r.data); })
      .catch(() => { if (!cancelled) setFullLead(null); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [id]);

  const lead = fullLead || lightLead;

  // No id in the URL — direct visit to /loan-summary. Surface a picker so
  // the user can jump to a real lead instead of seeing stale mock data.
  if (!id) {
    return <NoLeadPicker leadsLoaded={leadsLoaded} leads={allLeads} onPick={(lid) => navigate(`/loans/${lid}/summary`)} />;
  }

  // Lead store not yet loaded — show a loading skeleton.
  if ((!leadsLoaded && !lead) || (fetching && !lead)) {
    return (
      <div className="max-w-[1200px] mx-auto space-y-5">
        <div className="h-12 rounded-md bg-neutral-100 animate-pulse w-2/3" />
        <div className="h-64 rounded-2xl border hairline bg-white animate-pulse" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="max-w-[1200px] mx-auto py-16 text-center">
        <div className="display text-[24px] font-bold text-neutral-900 tracking-tight">Lead not found</div>
        <p className="text-[13px] text-neutral-500 mt-2">It may have been reassigned, deleted, or you don't have access.</p>
        <Link to="/leads" className="inline-flex items-center gap-1.5 mt-4 text-[12.5px] font-semibold text-neutral-900 gold-underline">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to leads
        </Link>
      </div>
    );
  }

  const a = lead.application || {};
  const li = a.loanInfo || {};

  // The mailer importer and the Lead Profile form both write FLAT keys on
  // `application` (loanAmount, lender, loanDate, rate, loanTerm, ...). We
  // read those flat keys first and fall back to the legacy nested
  // `application.loanInfo.*` shape so older or LE-imported leads still
  // render. Anything still missing shows "—".
  const pick = (...keys) => {
    for (const k of keys) {
      if (k !== undefined && k !== null && k !== '') return k;
    }
    return undefined;
  };
  const loanAmount   = pick(a.loanAmount, li.loanAmount, lead.estAmount);
  const loanType     = pick(a.loanType, li.loanType);
  const loanPurpose  = pick(a.loanPurpose, li.loanPurpose, lead.loanIntent);
  // Mailer import stores `application.term` as years (string, e.g. "30").
  // Lead Profile form stores `application.loanTerm` as months (number, e.g. 360).
  // Normalize to months for math + display.
  const loanTermMonths = (() => {
    const lt = Number(a.loanTerm ?? li.loanTerm);
    if (Number.isFinite(lt) && lt > 0) return lt > 60 ? lt : lt * 12; // bare years like 30 → 360
    const yrs = Number(a.term ?? li.term);
    if (Number.isFinite(yrs) && yrs > 0) return yrs * 12;
    return 0;
  })();
  const rate         = pick(a.rate, li.lockedRate, li.interestRate);
  const apr          = pick(a.apr, li.apr);
  const lockStatus   = pick(a.lockStatus, li.lockStatus);
  const lockExp      = pick(a.lockExpiration, li.lockExpiration);
  const titleOrderNo = pick(a.titleOrderNumber, li.titleOrderNumber);
  const lender       = pick(a.lender, lead.lender);
  const loanDate     = pick(a.loanDate, lead.loanDate);
  const occupancy    = pick(a.occupancy, a.occupancyType, a.propertyUse);

  // Monthly payment — derive P&I from the form fields. Taxes/insurance/HOA
  // come from the Property section (monthly amounts on the 1003).
  const monthlyPI = (() => {
    const amt = Number(loanAmount);
    const r = Number(rate);
    const term = Number(loanTermMonths);
    if (!amt || !r || !term) return null;
    const mr = r / 100 / 12;
    return (amt * mr) / (1 - Math.pow(1 + mr, -term));
  })();
  const taxes = pick(a.taxes, li.taxes);
  const insurance = pick(a.insurance, li.hazardInsurance);
  const hoa = pick(a.hoa, li.hoa);
  const monthlyTotal = (() => {
    const sum = [monthlyPI, taxes, insurance, hoa]
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v > 0)
      .reduce((s, v) => s + v, 0);
    return sum > 0 ? sum : pick(li.totalMonthlyPayment);
  })();

  const rows = [
    ['Borrower', fullName(lead)],
    ['Date of Birth', fmt(a.dob)],
    ['Property Address', propertyAddressOf(lead)],
    ['Property Type', fmt(a.propertyType)],
    ['Property Use', fmt(occupancy)],
    ['Loan Amount', money(loanAmount)],
    ['Loan Type', fmt(loanType)],
    ['Loan Purpose', fmt(loanPurpose)],
    ['Loan Term', loanTermMonths ? `${loanTermMonths} months (${Math.round(loanTermMonths / 12)} yr)` : '—'],
    ['Interest Rate', num(rate)],
    ['Annual Percentage Rate', num(apr)],
    ['Lock Status', fmt(lockStatus)],
    ['Lock Expiration Date', fmt(lockExp)],
    ['Lender', fmt(lender)],
    ['Original Loan Date', fmt(loanDate)],
    ['Title Order Number', fmt(titleOrderNo)],
  ];

  const payment = [
    ['Principal & Interest', monthlyPI],
    ['Taxes', taxes],
    ['Hazard Insurance', insurance],
    ['HOA', hoa],
    ['Total Monthly Payment', monthlyTotal],
  ];

  const loanNumber = lead.referenceId || lead.id || '—';

  return (
    <div className="max-w-[1200px] mx-auto space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11.5px] text-neutral-500 mono">Loan #{loanNumber}</div>
          <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Loan Summary</h1>
          <p className="text-[13px] text-neutral-500">
            {fullName(lead)} · <Link to={`/leads/${lead.id}`} className="gold-underline">Open lead profile</Link>
          </p>
        </div>
        <div className="flex gap-2">
          <button className="h-9 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-700 flex items-center gap-1.5"><Download className="h-4 w-4" /> PDF</button>
          <button className="h-9 px-3 rounded-md border hairline bg-white text-[12px] font-semibold text-neutral-700 flex items-center gap-1.5"><Printer className="h-4 w-4" /> Print</button>
          <button className="h-9 px-3 rounded-md btn-ink text-[12px] font-semibold flex items-center gap-1.5"><Share2 className="h-4 w-4" /> Share</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border hairline bg-white overflow-hidden">
          <div className="px-6 py-4 border-b hairline">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Loan file</div>
            <h3 className="display text-[18px] font-bold text-neutral-900">Terms & Party Information</h3>
          </div>
          <div className="divide-y divide-neutral-100">
            {rows.map(([k, v]) => (
              <div key={k} className="px-6 py-3 flex items-start gap-6 hover:bg-neutral-50/70">
                <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold w-56 shrink-0 mt-1">{k}</div>
                <div className="text-[13.5px] font-bold text-neutral-900 flex-1">{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border hairline bg-white p-6">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Monthly payment</div>
            <div className="display text-[30px] font-bold text-neutral-900 tracking-tight mt-0.5 mono">{money(monthlyTotal)}</div>
            <div className="mt-4 space-y-3">
              {payment.map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-[13px]">
                  <span className={k === 'Total Monthly Payment' ? 'font-bold text-neutral-900' : 'text-neutral-600'}>{k}</span>
                  <span className={`mono ${k === 'Total Monthly Payment' ? 'text-neutral-900 font-bold' : 'text-neutral-800 font-semibold'}`}>{money(v)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border hairline bg-neutral-900 text-white p-6 relative overflow-hidden noise">
            <div className="absolute inset-0 bg-dot-dark opacity-30" />
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[hsl(var(--kf-gold))]/20 blur-2xl" />
            <div className="relative">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold))] font-bold">Cash to close</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="display text-[28px] font-bold text-white tracking-tight mono">{money(pick(a.currentCashFromBorrower, li.currentCashFromBorrower))}</span>
                <span className="text-[11.5px] text-white/60">from borrower</span>
              </div>
              <div className="mt-3 text-[12px] text-white/70">
                Initial: <span className="mono font-bold text-white">{money(pick(a.initialCashToClose, li.initialCashToClose))}</span>
              </div>
              <div className="mt-3 text-[10.5px] text-white/50">*Subject to change until Final CD is disclosed.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoLeadPicker({ leadsLoaded, leads, onPick }) {
  const recent = (leads || []).slice(0, 6);
  return (
    <div className="max-w-[760px] mx-auto py-12 text-center">
      <div className="mx-auto h-12 w-12 rounded-xl border hairline bg-neutral-50 flex items-center justify-center">
        <Inbox className="h-5 w-5 text-neutral-500" />
      </div>
      <h1 className="display text-[28px] font-bold text-neutral-900 tracking-tight mt-4">Pick a loan</h1>
      <p className="text-[13px] text-neutral-500 mt-1">Loan Summary is per-lead. Choose one from your pipeline to see its terms.</p>
      {!leadsLoaded ? (
        <div className="h-24 mt-6 rounded-xl border hairline bg-white animate-pulse" />
      ) : recent.length === 0 ? (
        <div className="mt-6 rounded-xl border hairline bg-white p-6 text-[13px] text-neutral-500">
          No leads in your pipeline yet. Upload a mailer or create a lead first.
        </div>
      ) : (
        <div className="mt-6 grid gap-2 text-left">
          {recent.map((l) => (
            <button
              key={l.id}
              onClick={() => onPick(l.id)}
              className="rounded-xl border hairline bg-white hover:border-[hsl(var(--kf-gold))] hover:bg-neutral-50 transition px-4 py-3 flex items-center justify-between"
            >
              <div>
                <div className="text-[13.5px] font-bold text-neutral-900">{fullName(l)}</div>
                <div className="text-[11.5px] text-neutral-500 mono">{l.referenceId || l.id}</div>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-neutral-400 font-bold">{l.loanStatus || l.temp || '—'}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
